/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/Modals/Araclar/AracEkleModal.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  useToast,
  SimpleGrid,
  FormHelperText,
  Box,
  List,
  ListItem,
  Icon,
  Switch,
} from "@chakra-ui/react";
import api from "utils/api";
import { getCurrentUser } from "services/authService";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

// FilterPanel / FirmSelectorWithFilters ile aynı Türkçe arama
const normalizeSearchText = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();

export default function AracEkleModal({
  isOpen,
  onClose,
  onSave,
  kilitliFirma = null,
  bayiler = [], // ✅ Parent'tan prop olarak al
}) {
  const toast = useToast();

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const initialForm = useMemo(
    () => ({
    plaka: "",
    abonelikTipi: "",
    firma: kilitliFirma || "",
    cihazTipi: "",
    aracTipi: "default",
    montajPersoneli: "",
    imei: "",
    satisTarihi: today,
    satisTuru: "Satılık",
    kullanim: "Kullanımda",
    aksesuarId: "",
    aksesuarAdet: "1",
    bayiId: "",
    uetdsYetkilendirme: false,
    }),
    [kilitliFirma, today]
  );

  const [form, setForm] = useState(initialForm);
  const [firmaOptions, setFirmaOptions] = useState([]); // Firma isimleri listesi
  const [firmaDataMap, setFirmaDataMap] = useState({}); // Firma ismi -> firma verisi mapping
  const [personelOptions] = useState(["Ahmet", "Mehmet", "Ali"]);
  const [userRole, setUserRole] = useState(null);
  const [imeiOptions, setImeiOptions] = useState([]);
  const [imeiLoading, setImeiLoading] = useState(false);
  const [imeiSearchQuery, setImeiSearchQuery] = useState(""); // IMEI arama sorgusu
  const [showImeiList, setShowImeiList] = useState(false); // IMEI liste görünürlüğü
  const imeiInputRef = useRef(null);
  const imeiListRef = useRef(null);
  const [firmaSearchQuery, setFirmaSearchQuery] = useState(""); // Firma arama sorgusu
  const [showFirmaList, setShowFirmaList] = useState(false); // Firma liste görünürlüğü
  const firmaInputRef = useRef(null);
  const firmaListRef = useRef(null);
  const [aksesuarOptions, setAksesuarOptions] = useState([]);
  const [aksesuarLoading, setAksesuarLoading] = useState(false);
  const [showOptionalSection, setShowOptionalSection] = useState(false);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserRole(user.role);
      })
      .catch(() => {
        setUserRole(null);
      });
  }, []);

  // IMEI yükleme fonksiyonu
  const loadImeis = useCallback(async (selectedBayiId = null) => {
    setImeiLoading(true);
    try {
      // Admin için: Bayi seçilmişse o bayiye ait, seçilmemişse admin'in kendi cihazlarını getir
      let url = "/api/stok-cihazlari/?durum=hazir";
      if (userRole === "admin") {
        if (selectedBayiId) {
          // Bayi seçilmişse, o bayiye ait cihazları getir
          url += `&bayi_id=${selectedBayiId}`;
        }
        // Bayi seçilmemişse, backend otomatik olarak admin'in kendi cihazlarını (bayi_id IS NULL) getirir
      }
      
      const res = await api.get(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Stok bilgisi alınamadı.");
      
      const options = (Array.isArray(data) ? data : [])
        .filter((item) => item.durum === "hazir" && !item.in_use)
        .map((item) => ({
          value: item.imei,
          label: item.model ? `${item.imei} • ${item.model}` : item.imei,
          model: item.model || "",
          protokol: item.protokol || "",
          bataryali: item.bataryali || false,
        }));
      setImeiOptions(options);
    } catch (err) {
      setImeiOptions([]);
      toast({
        title: "Stok okunamadı",
        description: err.message,
        status: "error",
      });
    } finally {
      setImeiLoading(false);
    }
  }, [userRole, toast]);

  // Firma yükleme fonksiyonu
  const loadFirmalar = useCallback(async (selectedBayiId = null, currentFirma = null) => {
    if (kilitliFirma) {
      // Kilitli firma varsa firma listesi yüklenmez
      return;
    }

    try {
      const params = new URLSearchParams({ select_only: "true" });
      if (userRole === "admin" && selectedBayiId) {
        params.set("bayi_id", String(selectedBayiId));
      }
      const url = `/api/firmalar?${params.toString()}`;
      // Bayi seçilmemişse veya admin değilse, tüm firmaları getir (backend role-based filtering yapar)
      
      const res = await api.get(url);
      const data = await res.json();
      if (!res.ok) throw new Error();
      
      const firms = Array.isArray(data)
        ? data
            .filter(
              (f) =>
                !String(f.firma_durumu || "")
                  .toLowerCase()
                  .startsWith("pasif")
            )
        : [];
      
      // Firma isimleri listesi
      const firmNames = firms.map((f) => f.firma_ismi || f.firma).filter(Boolean);
      setFirmaOptions(firmNames);
      
      // Firma verisi mapping (firma ismi -> firma objesi)
      const firmaMap = {};
      firms.forEach((f) => {
        const firmaName = f.firma_ismi || f.firma;
        if (firmaName) {
          firmaMap[firmaName] = f;
        }
      });
      setFirmaDataMap(firmaMap);
      
      // Eğer seçili firma yeni listede yoksa, firma seçimini temizle
      if (currentFirma && !firmNames.includes(currentFirma)) {
        setForm((prev) => ({ ...prev, firma: "" }));
      }
    } catch (err) {
      setFirmaOptions([]);
    }
  }, [userRole, kilitliFirma]);

  // IMEI listesini filtrele
  const filteredImeiOptions = useMemo(() => {
    if (!imeiSearchQuery) return imeiOptions;
    const query = imeiSearchQuery.toLowerCase();
    return imeiOptions.filter(
      (opt) =>
        opt.value.toLowerCase().includes(query) ||
        opt.label.toLowerCase().includes(query)
    );
  }, [imeiOptions, imeiSearchQuery]);

  // Firma listesini filtrele
  const filteredFirmaOptions = useMemo(() => {
    const normalizedQuery = normalizeSearchText(firmaSearchQuery);
    if (!normalizedQuery) return firmaOptions;
    return firmaOptions.filter((firma) =>
      normalizeSearchText(firma).includes(normalizedQuery)
    );
  }, [firmaOptions, firmaSearchQuery]);

  // Dışarı tıklandığında IMEI listesini kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        imeiInputRef.current &&
        imeiListRef.current &&
        !imeiInputRef.current.contains(event.target) &&
        !imeiListRef.current.contains(event.target)
      ) {
        setShowImeiList(false);
      }
    };

    if (showImeiList) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showImeiList]);

  // Dışarı tıklandığında firma listesini kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        firmaInputRef.current &&
        firmaListRef.current &&
        !firmaInputRef.current.contains(event.target) &&
        !firmaListRef.current.contains(event.target)
      ) {
        setShowFirmaList(false);
      }
    };

    if (showFirmaList) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFirmaList]);

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm);
      setImeiSearchQuery(""); // Modal açıldığında arama sorgusunu temizle
      setShowImeiList(false); // Liste görünürlüğünü kapat
      setFirmaSearchQuery(""); // Firma arama sorgusunu temizle
      setShowFirmaList(false); // Firma liste görünürlüğünü kapat
      setShowOptionalSection(false);

      const loadAksesuarlar = async () => {
        setAksesuarLoading(true);
        try {
          const res = await api.get("/api/aksesuarlar/");
          const data = await res.json();
          if (!res.ok) throw new Error(data?.detail || "Aksesuarlar alınamadı.");
          const options = (Array.isArray(data) ? data : []).map((item) => ({
            id: item.id,
            name: item.name,
          }));
          setAksesuarOptions(options);
        } catch (err) {
          setAksesuarOptions([]);
          toast({
            title: "Aksesuar listesi alınamadı",
            description: err.message,
            status: "error",
          });
        } finally {
          setAksesuarLoading(false);
        }
      };

      // Modal açıldığında başlangıçta IMEI'leri ve firmaları yükle (henüz bayi seçilmemiş)
      loadImeis(null);
      loadFirmalar(null);
      loadAksesuarlar();
      
      // Ref'leri temizle
      prevBayiIdRef.current = null;
      prevFirmaRef.current = null;
      isAutoBayiSelectionRef.current = false;
    }
  }, [isOpen, initialForm, toast, loadImeis, loadFirmalar]);
  
  // Bayi seçimi değiştiğinde IMEI ve firma listesini yeniden yükle
  // İlk yüklemede çalışmaması için: form.bayiId değiştiğinde VE modal zaten açıkken
  const prevBayiIdRef = useRef(null);
  const isAutoBayiSelectionRef = useRef(false); // Otomatik bayi seçimi yapıldığını işaretle
  useEffect(() => {
    // Modal ilk açıldığında prevBayiIdRef null olacak, bu durumda çalışma
    if (!isOpen || userRole !== "admin") {
      prevBayiIdRef.current = form.bayiId;
      return;
    }
    
    // Eğer bayiId gerçekten değiştiyse (hem otomatik hem manuel seçimde)
    if (prevBayiIdRef.current !== null && prevBayiIdRef.current !== form.bayiId) {
      const selectedBayiId = form.bayiId ? parseInt(form.bayiId) : null;
      
      // IMEI listesini her zaman yeniden yükle (otomatik veya manuel seçimde)
      loadImeis(selectedBayiId);
      
      // Firma listesini sadece manuel bayi seçiminde yeniden yükle (otomatik seçimde yükleme)
      if (!isAutoBayiSelectionRef.current) {
        loadFirmalar(selectedBayiId, form.firma);
      } else {
        // Otomatik seçim yapıldıysa flag'i temizle
        isAutoBayiSelectionRef.current = false;
      }
    }
    
    prevBayiIdRef.current = form.bayiId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.bayiId, isOpen, userRole, loadImeis, loadFirmalar]);

  // IMEI seçildiğinde stok kaydındaki model bilgisini otomatik çek
  useEffect(() => {
    if (form.imei) {
      const selected = imeiOptions.find((option) => option.value === form.imei);
      if (selected && selected.model) {
        setForm((prev) => ({ ...prev, cihazTipi: selected.model }));
      }
      
      // Test modunda plaka otomatik IMEI ile aynı olsun (eğer kullanıcı manuel değiştirmemişse)
      if (form.satisTuru === "Test") {
        setForm((prev) => {
          // Eğer plaka boşsa veya IMEI ile aynıysa (otomatik doldurulmuşsa), IMEI ile güncelle
          if (!prev.plaka || prev.plaka === prev.imei) {
            return { ...prev, plaka: form.imei };
          }
          // Kullanıcı manuel plaka girmişse, onu koru
          return prev;
        });
      }
    }
  }, [form.imei, imeiOptions, form.satisTuru]);
  
  // Test modu değiştiğinde plaka'yı güncelle
  useEffect(() => {
    if (form.satisTuru === "Test" && form.imei) {
      // Test moduna geçildiğinde plaka IMEI ile aynı olsun (eğer boşsa veya IMEI ile aynıysa)
      setForm((prev) => {
        // Eğer plaka boşsa veya IMEI ile aynıysa (otomatik doldurulmuşsa), IMEI ile güncelle
        if (!prev.plaka || prev.plaka === prev.imei) {
          return { ...prev, plaka: form.imei };
        }
        // Kullanıcı manuel plaka girmişse, onu koru
        return prev;
      });
    } else if (form.satisTuru !== "Test") {
      // Test modundan çıkıldığında, eğer plaka IMEI ile aynıysa (otomatik doldurulmuşsa) temizle
      setForm((prev) => {
        if (prev.plaka === prev.imei) {
          return { ...prev, plaka: "" };
        }
        return prev;
      });
    }
  }, [form.satisTuru, form.imei]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "satisTuru" && value === "Kiralık") {
        next.abonelikTipi = "1 Ay";
      } else if (field === "satisTuru" && value === "Test") {
        next.abonelikTipi = "";
      }
      return next;
    });
  };
  
  // ✅ Firma seçildiğinde otomatik bayi seçimi (sadece admin için)
  const prevFirmaRef = useRef(null);
  useEffect(() => {
    // Sadece admin için ve firma gerçekten değiştiğinde çalış
    if (userRole === "admin" && form.firma && form.firma !== prevFirmaRef.current && firmaDataMap[form.firma]) {
      const selectedFirma = firmaDataMap[form.firma];
      // Eğer firma bir bayiye aitse, otomatik olarak o bayi seçilsin
      if (selectedFirma.bayi_id) {
        const bayiIdStr = String(selectedFirma.bayi_id);
        // Bayi seçimini güncelle (otomatik olduğunu işaretle)
        isAutoBayiSelectionRef.current = true; // Otomatik seçim yapıldığını işaretle
        setForm((prev) => ({ ...prev, bayiId: bayiIdStr }));
      } else {
        // Firma admin'e aitse (bayi_id null), bayi seçimini temizle
        isAutoBayiSelectionRef.current = true; // Otomatik seçim yapıldığını işaretle
        setForm((prev) => ({ ...prev, bayiId: "" }));
      }
      // Firma seçildiğinde arama sorgusunu temizle
      setFirmaSearchQuery("");
      setShowFirmaList(false);
    }
    prevFirmaRef.current = form.firma;
  }, [form.firma, firmaDataMap, userRole]);

  const handleSave = async () => {
    const abonelikRequired = form.satisTuru !== "Test";
    if (
      !form.plaka ||
      (abonelikRequired && !form.abonelikTipi) ||
      !form.firma ||
      !form.montajPersoneli ||
      !form.imei ||
      !form.aracTipi
    ) {
      toast({
        title: "Eksik bilgi",
        description: "Lütfen zorunlu alanları doldurun.",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    try {
      const imeiValid = imeiOptions.some((opt) => opt.value === form.imei);
      if (!imeiValid) {
        toast({
          title: "Geçersiz IMEI",
          description: "Lütfen hazır stoktan bir IMEI seçin.",
          status: "warning",
        });
        return;
      }

      if (form.aksesuarId) {
        const selected = aksesuarOptions.find((opt) => String(opt.id) === form.aksesuarId);
        if (!selected) {
          toast({
            title: "Aksesuar bulunamadı",
            status: "warning",
          });
          return;
        }
        const parsedAdet = Number(form.aksesuarAdet);
        if (!Number.isInteger(parsedAdet) || parsedAdet < 1) {
          toast({
            title: "Geçersiz aksesuar adedi",
            description: "Aksesuar adedi en az 1 olmalıdır.",
            status: "warning",
          });
          return;
        }
      }

      // Backend schema'sına uygun format
      const payload = {
        plaka: form.plaka,
        imei: form.imei,
        firma: form.firma,
        cihaz_tipi: form.cihazTipi,
        arac_tipi: form.aracTipi || "default",
        montaj_personeli: form.montajPersoneli,
        satis_tarihi: form.satisTuru === "Test" ? null : form.satisTarihi,
        abonelik_tipi: form.satisTuru === "Test" ? null : form.abonelikTipi,
        satis_turu: form.satisTuru === "Test" ? "Test" : form.satisTuru,
        bayi_id: form.bayiId ? parseInt(form.bayiId) : null,
      };
      if (form.aksesuarId) {
        payload.aksesuar_id = Number(form.aksesuarId);
        payload.aksesuar_adet = Number(form.aksesuarAdet || 1);
      }

      // ✅ Doğru endpoint: /arac-ekle
      const response = await api.post("/api/arac-ekle", payload);

      if (!response.ok) {
        throw new Error(`Araç kaydedilemedi: ${response.status}`);
      }

      const data = await response.json();
      const deviceId = data?.device?.id;

      let uetdsOk = null;
      if (userRole === "admin" && form.uetdsYetkilendirme && deviceId != null) {
        try {
          // Backend: GET device → uetds=true merge → PUT (partial gövde alanları ezmez)
          const uetdsRes = await api.put(`/api/devices/${deviceId}`, { uetds: true });
          if (!uetdsRes.ok) {
            let detail = `UETDS açılamadı: ${uetdsRes.status}`;
            try {
              const errBody = await uetdsRes.json();
              detail = errBody?.detail || errBody?.message || detail;
            } catch (_) {
              /* ignore */
            }
            throw new Error(detail);
          }
          uetdsOk = true;
        } catch (uetdsErr) {
          uetdsOk = false;
          toast({
            title: "Araç eklendi, UETDS açılamadı",
            description: uetdsErr.message || "Admin oturumu / Traccar hatası",
            status: "warning",
            duration: 6000,
            isClosable: true,
            position: "top-right",
          });
        }
      }

      toast({
        title: "Başarılı",
        description:
          uetdsOk === true
            ? "Araç eklendi; UETDS yetkilendirme açıldı"
            : "Araç başarıyla eklendi ve Traccar'a atandı!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      onSave?.(data);
      onClose();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" maxW="520px" bg="white" p={1}>
        <ModalHeader textAlign="center" py={2}>
          <VStack spacing={1}>
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Yeni Araç Ekle
            </Text>
            <Text fontSize="xs" color="gray.400">
              Araç bilgilerini girin
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton size="sm" />

        <ModalBody px={6} py={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Plaka */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Plaka</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                placeholder={form.satisTuru === "Test" ? "IMEI ile otomatik doldurulur" : "34ABC123"}
                value={form.plaka}
                onChange={(e) => handleChange("plaka", e.target.value.toLocaleUpperCase("tr-TR"))}
              />
              {form.satisTuru === "Test" && (
                <FormHelperText fontSize="xs" color="gray.500">
                  Test modunda plaka otomatik olarak IMEI ile aynı olur, isterseniz değiştirebilirsiniz.
                </FormHelperText>
              )}
            </FormControl>

            {/* Abonelik Tipi */}
            <FormControl isDisabled={form.satisTuru === "Test"}>
              <FormLabel fontSize="sm" color="gray.600">Abonelik Tipi</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                placeholder="Seçiniz"
                value={form.abonelikTipi}
                onChange={(e) => handleChange("abonelikTipi", e.target.value)}
                isDisabled={form.satisTuru === "Kiralık" || form.satisTuru === "Test"}
              >
                <option value="1 Ay">1 Ay</option>
                <option value="12 Ay">12 Ay</option>
                <option value="24 Ay">24 Ay</option>
                <option value="36 Ay">36 Ay</option>
                <option value="Demo">Demo</option>
              </Select>
              {(form.satisTuru === "Kiralık" || form.satisTuru === "Test") && (
                <FormHelperText fontSize="xs" color="gray.500">
                  {form.satisTuru === "Kiralık"
                    ? "Kiralık satışlarda abonelik süresi otomatik 1 Ay olarak ayarlandı."
                    : "Test modunda abonelik bilgisi gerekmez; montaj sonrası güncellenir."}
                </FormHelperText>
              )}
            </FormControl>

            {/* Satış / Kullanım Türü */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">
                Satış / Kullanım Türü
              </FormLabel>
              <Select
                variant="flushed"
                size="sm"
                value={form.satisTuru}
                onChange={(e) => handleChange("satisTuru", e.target.value)}
              >
                <option value="Satılık">Satış</option>
                <option value="Kiralık">Kiralama</option>
                <option value="Test">Test</option>
              </Select>
            </FormControl>

            {/* Firma */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Firma</FormLabel>
              {kilitliFirma ? (
                <Input
                  variant="flushed"
                  size="sm"
                  value={kilitliFirma}
                  isReadOnly
                />
              ) : (
                <Box ref={firmaInputRef} position="relative">
                  <Input
                    variant="flushed"
                    size="sm"
                    placeholder="Firma yazın veya seçin"
                    value={firmaSearchQuery !== "" ? firmaSearchQuery : form.firma}
                    onChange={(e) => {
                      const query = e.target.value;
                      setFirmaSearchQuery(query);
                      setShowFirmaList(true);
                      
                      // Tam eşleşme varsa otomatik seç
                      const exactMatch = firmaOptions.find(
                        (f) => normalizeSearchText(f) === normalizeSearchText(query)
                      );
                      if (exactMatch) {
                        handleChange("firma", exactMatch);
                        setFirmaSearchQuery("");
                        setShowFirmaList(false);
                      } else if (!query) {
                        handleChange("firma", "");
                        setFirmaSearchQuery("");
                        setShowFirmaList(false);
                      }
                    }}
                    onFocus={() => {
                      if (firmaSearchQuery || firmaOptions.length > 0) {
                        setShowFirmaList(true);
                      }
                    }}
                  />
                  
                  {/* Firma Liste Dropdown */}
                  {showFirmaList && filteredFirmaOptions.length > 0 && (
                    <Box
                      ref={firmaListRef}
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      zIndex={1000}
                      mt={1}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="200px"
                      overflowY="auto"
                    >
                      <List spacing={0}>
                        {filteredFirmaOptions.slice(0, 50).map((firma) => (
                          <ListItem
                            key={firma}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: "gray.50" }}
                            onClick={() => {
                              handleChange("firma", firma);
                              setFirmaSearchQuery("");
                              setShowFirmaList(false);
                            }}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            fontSize="sm"
                          >
                            <Text fontWeight="medium" color="gray.800">
                              {firma}
                            </Text>
                          </ListItem>
                        ))}
                        {filteredFirmaOptions.length > 50 && (
                          <ListItem
                            px={3}
                            py={2}
                            fontSize="xs"
                            color="gray.500"
                            textAlign="center"
                            bg="gray.50"
                          >
                            {filteredFirmaOptions.length - 50} sonuç daha var...
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>

            {/* IMEI seçimi */}
            <FormControl isRequired position="relative">
              <FormLabel fontSize="sm" color="gray.600">IMEI</FormLabel>
              <Box ref={imeiInputRef} position="relative">
                <Input
                  variant="flushed"
                  size="sm"
                  placeholder={imeiLoading ? "Yükleniyor..." : "IMEI yazın veya seçin"}
                  value={imeiSearchQuery !== "" ? imeiSearchQuery : form.imei}
                  onChange={(e) => {
                    const query = e.target.value;
                    setImeiSearchQuery(query);
                    setShowImeiList(true);
                    
                    // Tam eşleşme varsa otomatik seç
                    const exactMatch = imeiOptions.find((opt) => opt.value === query);
                    if (exactMatch) {
                      handleChange("imei", exactMatch.value);
                      setImeiSearchQuery("");
                      setShowImeiList(false);
                    } else if (!query) {
                      handleChange("imei", "");
                      setImeiSearchQuery("");
                      setShowImeiList(false);
                    }
                  }}
                  onFocus={() => {
                    if (imeiSearchQuery || imeiOptions.length > 0) {
                      setShowImeiList(true);
                    }
                  }}
                  isDisabled={imeiLoading}
                />
                
                {/* IMEI Liste Dropdown */}
                {showImeiList && filteredImeiOptions.length > 0 && (
                  <Box
                    ref={imeiListRef}
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    zIndex={1000}
                    mt={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    maxH="200px"
                    overflowY="auto"
                  >
                    <List spacing={0}>
                      {filteredImeiOptions.slice(0, 50).map((option) => (
                        <ListItem
                          key={option.value}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: "gray.50" }}
                          onClick={() => {
                            handleChange("imei", option.value);
                            setImeiSearchQuery("");
                            setShowImeiList(false);
                          }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          fontSize="sm"
                        >
                          <Text fontWeight="medium" color="gray.800">
                            {option.value}
                          </Text>
                          {option.model && (
                            <Text fontSize="xs" color="gray.500">
                              {option.model}
                            </Text>
                          )}
                        </ListItem>
                      ))}
                      {filteredImeiOptions.length > 50 && (
                        <ListItem
                          px={3}
                          py={2}
                          fontSize="xs"
                          color="gray.500"
                          textAlign="center"
                          bg="gray.50"
                        >
                          {filteredImeiOptions.length - 50} sonuç daha var...
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}
              </Box>
              {!imeiLoading && !imeiOptions.length && (
                <FormHelperText fontSize="xs" color="red.500">
                  Hazır cihaz stoğu bulunamadı. Önce stok ekleyin.
                </FormHelperText>
              )}
              {form.cihazTipi && (
                <FormHelperText fontSize="xs" color="gray.500">
                  Model: {form.cihazTipi}
                </FormHelperText>
              )}
              {form.imei && (() => {
                const selected = imeiOptions.find((option) => option.value === form.imei);
                if (selected && selected.bataryali) {
                  return (
                    <FormHelperText fontSize="xs" color="blue.500" fontWeight="medium">
                      ⚡ Bu cihaz bataryalıdır
                    </FormHelperText>
                  );
                }
                return null;
              })()}
            </FormControl>

            {/* Araç Tipi - Traccar category (zorunlu) */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" color="gray.600">Araç Tipi</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                placeholder="Seçiniz"
                value={form.aracTipi}
                onChange={(e) => handleChange("aracTipi", e.target.value)}
              >
                <option value="default">Varsayılan</option>
                <option value="car">Araba</option>
                <option value="truck">Kamyon</option>
                <option value="motorcycle">Motosiklet</option>
                <option value="bicycle">Bisiklet</option>
                <option value="boat">Tekne</option>
                <option value="plane">Uçak</option>
                <option value="helicopter">Helikopter</option>
                <option value="person">İnsan</option>
                <option value="animal">Hayvan</option>
              </Select>
            </FormControl>

            {/* Montaj Personeli */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Montaj Personeli</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                placeholder="Personel seçiniz"
                value={form.montajPersoneli}
                onChange={(e) => handleChange("montajPersoneli", e.target.value)}
              >
                {personelOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </Select>
            </FormControl>

            {/* Satış Tarihi */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Satış Tarihi</FormLabel>
              <Input
                type="date"
                variant="flushed"
                size="sm"
                value={form.satisTarihi}
                onChange={(e) => handleChange("satisTarihi", e.target.value)}
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Maksimum 15 karakter seçilebilir.
              </FormHelperText>
            </FormControl>

          </SimpleGrid>

          {userRole === "admin" && (
            <FormControl
              mt={4}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              borderTop="1px solid"
              borderColor="gray.100"
              pt={3}
            >
              <Box pr={3}>
                <FormLabel fontSize="sm" color="gray.700" mb={0} fontWeight="medium">
                  UETDS Yetkilendirme
                </FormLabel>
                <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                  Açıkken cihazda device.uetds=true yapılır. Tam bildirim için hesap UETDS de açık olmalı.
                </FormHelperText>
              </Box>
              <Switch
                colorScheme="green"
                isChecked={form.uetdsYetkilendirme}
                onChange={(e) => handleChange("uetdsYetkilendirme", e.target.checked)}
              />
            </FormControl>
          )}

          <Box mt={5}>
            <Button
              variant="ghost"
              size="sm"
              w="100%"
              justifyContent="space-between"
              onClick={() => setShowOptionalSection((prev) => !prev)}
              rightIcon={<Icon as={showOptionalSection ? FiChevronUp : FiChevronDown} />}
            >
              Opsiyonel Alanlar
            </Button>

            {showOptionalSection && (
              <VStack align="stretch" spacing={4} mt={2}>
                {userRole === "admin" && bayiler && bayiler.length > 0 && (
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Bayi (Opsiyonel)</FormLabel>
                    <Select
                      variant="flushed"
                      size="sm"
                      placeholder="Bayi seçiniz (boş bırakılırsa admin'e eklenir)"
                      value={form.bayiId || ""}
                      onChange={(e) => handleChange("bayiId", e.target.value)}
                      color={form.bayiId ? "gray.800" : "gray.400"}
                    >
                      {bayiler.map((bayi) => (
                        <option key={bayi.id} value={bayi.id}>
                          {bayi.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">Aksesuar (Opsiyonel)</FormLabel>
                    <Select
                      variant="flushed"
                      size="sm"
                      placeholder={aksesuarLoading ? "Yükleniyor..." : "Seçiniz"}
                      value={form.aksesuarId}
                      onChange={(e) =>
                        handleChange("aksesuarId", e.target.value ? e.target.value : "")
                      }
                      isDisabled={aksesuarLoading || !aksesuarOptions.length}
                    >
                      {aksesuarOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl isDisabled={!form.aksesuarId}>
                    <FormLabel fontSize="sm" color="gray.600">Aksesuar Adedi</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      variant="flushed"
                      size="sm"
                      value={form.aksesuarAdet}
                      onChange={(e) => handleChange("aksesuarAdet", e.target.value)}
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}
          </Box>
        </ModalBody>

        <ModalFooter justifyContent="flex-end" px={6} py={3}>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
            İptal
          </Button>
          <Button
            size="sm"
            px={4}
            bgGradient="linear(to-r, gray.800, black)"
            color="white"
            _hover={{
              bgGradient: "linear(to-r, black, gray.900)",
              boxShadow: "0 0 8px rgba(0,0,0,0.4)",
            }}
            borderRadius="md"
            onClick={handleSave}
          >
            Kaydet
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}